import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import CompFreeDrilldown from '../components/CompFreeDrilldown';
import PastDueDrilldown from '../components/PastDueDrilldown';
import PodStatusOverviewCard from '../components/PodStatusOverviewCard';
import PodStatusSection from '../components/PodStatusSection';
import SubscribedDrilldown from '../components/SubscribedDrilldown';
import { loadPodDashboardData } from '../podDashboard.api';
import { POD_STATUS_ORDER, PodDashboardData } from '../podDashboard.types';

/**
 * Revamped /admin/pods dashboard: a single-screen view of every pod we own, by
 * status. Counts and per-org coverage come from live backend data
 * (podDashboard.api.ts); revenue is still placeholder pending a revenue source.
 */
const PodDashboard = () => {
    const [data, setData] = useState<PodDashboardData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        loadPodDashboardData()
            .then((result) => {
                if (active) setData(result);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    if (loading || !data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
            {/* Page header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                }}
            >
                <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.8rem' }}>
                        FiyrPod — Pod Management
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Every pod we own, by status. Click any row to drill down.
                    </Typography>
                </Box>
                <Button component={RouterLink} to="/admin/pods" variant="outlined" size="small">
                    Open pod list
                </Button>
            </Box>

            {/* Overview card */}
            <Box sx={{ mb: 3 }}>
                <PodStatusOverviewCard data={data} />
            </Box>

            {/* Status rows */}
            <Stack spacing={2}>
                {POD_STATUS_ORDER.map((key, idx) => {
                    const segment = data.segments.find((s) => s.key === key);
                    if (!segment) return null;
                    let drilldown: React.ReactNode = null;
                    if (key === 'subscribed') {
                        drilldown = <SubscribedDrilldown subscribed={data.subscribed} />;
                    } else if (key === 'past_due') {
                        drilldown = <PastDueDrilldown pastDue={data.pastDue} />;
                    } else if (key === 'comp_free') {
                        drilldown = <CompFreeDrilldown compFree={data.compFree} />;
                    }
                    return (
                        <PodStatusSection
                            key={key}
                            index={idx + 1}
                            segment={segment}
                            totalPods={data.totalPods}
                            defaultExpanded={key === 'subscribed'}
                        >
                            {drilldown}
                        </PodStatusSection>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default PodDashboard;
